import {
  listProvidersRequest,
  listAssetsRequest,
  createAssetRequest,
  updateAssetRequest,
  deleteAssetRequest,
  getPortfolioRequest,
  ratePreviewRequest,
} from "@/API/requests";
import { SUPPORTED_CURRENCIES, isSupportedCurrency } from "@/misc/utils";

export const initialPortfolio = () => ({
  supported_currencies: [...SUPPORTED_CURRENCIES],
  items: [],
  summaries_by_currency: {},
});

export function normalizePortfolio(snapshot) {
  const isRecord = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);
  const invalid = () =>
    new Error("We could not load a complete portfolio. Please try again.");
  if (
    !isRecord(snapshot) ||
    !Array.isArray(snapshot.items) ||
    (!isRecord(snapshot.summaries_by_currency) &&
      !(snapshot.items.length === 0 &&
        Array.isArray(snapshot.summaries_by_currency) &&
        snapshot.summaries_by_currency.length === 0)) ||
    (snapshot.supported_currencies != null &&
      !Array.isArray(snapshot.supported_currencies))
  ) {
    throw invalid();
  }
  const summaries = Array.isArray(snapshot.summaries_by_currency)
    ? {}
    : snapshot.summaries_by_currency;
  // Preserve the supported-currency boundary, but never turn malformed data into an empty account.
  if (
    snapshot.items.some(
      (item) => !isRecord(item) || typeof item.currency !== "string",
    )
  )
    throw invalid();
  const items = snapshot.items.filter((item) =>
    isSupportedCurrency(item.currency),
  );
  const supportedCurrencies = SUPPORTED_CURRENCIES.filter((currency) =>
    (snapshot.supported_currencies || SUPPORTED_CURRENCIES).includes(currency),
  );
  if (
    SUPPORTED_CURRENCIES.some(
      (currency) =>
        summaries[currency] != null &&
        (!isRecord(summaries[currency]) ||
          summaries[currency].currency !== currency),
    ) ||
    items.some(
      (item) =>
        !summaries[item.currency] ||
        !supportedCurrencies.includes(item.currency),
    )
  ) {
    throw invalid();
  }

  return {
    supported_currencies: supportedCurrencies,
    items,
    summaries_by_currency: Object.fromEntries(
      SUPPORTED_CURRENCIES.filter(
        (currency) => summaries[currency] != null,
      ).map((currency) => [currency, summaries[currency]]),
    ),
  };
}

const state = () => ({
  portfolio: initialPortfolio(),
  assets: [],
  providers: [],
  isLoading: false,
  error: null,
  newAssetDraft: null,
  selectedCurrencyView: null,
});

const mutations = {
  setLoading(s, v) {
    s.isLoading = v;
  },
  setError(s, err) {
    s.error = err;
  },
  // Actions validate snapshots before committing; a rejected response leaves the last good state intact.
  setPortfolio(s, portfolio) {
    s.portfolio = portfolio;
  },
  setAssets(s, assets) {
    s.assets = assets;
  },
  setProviders(s, providers) {
    s.providers = providers;
  },
  setNewAssetDraft(s, draft) {
    s.newAssetDraft = draft;
  },
  setSelectedCurrencyView(s, view) {
    s.selectedCurrencyView = isSupportedCurrency(view) ? view : null;
  },
};

let providersRequestId = 0;

// A successful write must not be repeated just because its response snapshot is incomplete.
async function applyMutationPortfolio({ commit, dispatch }, portfolio) {
  let normalized;
  try {
    normalized = normalizePortfolio(portfolio);
  } catch {
    try {
      return await dispatch("fetchPortfolio");
    } catch {
      const error = new Error(
        "Your change was saved, but we could not reload your portfolio. Please try loading your portfolio again.",
      );
      commit("setError", error.message);
      throw error;
    }
  }
  commit("setPortfolio", normalized);
  commit("setError", null);
  return portfolio;
}

const actions = {
  async fetchPortfolio({ commit }) {
    commit("setLoading", true);
    commit("setError", null);
    try {
      const portfolio = await getPortfolioRequest();
      commit("setPortfolio", normalizePortfolio(portfolio));
      return portfolio;
    } catch (error) {
      commit("setError", error?.response?.data?.message || error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },
  async listAssets({ commit }) {
    commit("setError", null);
    const assets = await listAssetsRequest();
    commit("setAssets", assets);
    return assets;
  },
  async listProviders({ commit }, params) {
    commit("setError", null);
    const requestId = ++providersRequestId;
    const providers = await listProvidersRequest(params);
    if (requestId === providersRequestId) commit("setProviders", providers);
    return providers;
  },
  async createAsset({ commit }, payload) {
    const portfolio = await createAssetRequest(payload);
    let normalized;
    try {
      normalized = normalizePortfolio(portfolio);
    } catch {
      // The POST succeeded. Do not invite another POST if only its snapshot is invalid.
      // The add page navigates to the tracker, which fetches and validates a fresh snapshot.
      commit(
        "setError",
        "Your asset was saved, but its portfolio could not be loaded.",
      );
      return portfolio;
    }
    commit("setPortfolio", normalized);
    commit("setError", null);
    return portfolio;
  },
  async updateAsset(context, { id, payload }) {
    const portfolio = await updateAssetRequest(id, payload);
    return applyMutationPortfolio(context, portfolio);
  },
  async deleteAsset(context, id) {
    const portfolio = await deleteAssetRequest(id);
    return applyMutationPortfolio(context, portfolio);
  },
  async ratePreview(_, params) {
    return await ratePreviewRequest(params);
  },
};

const getters = {
  portfolio: (s) => s.portfolio,
  assets: (s) => s.assets,
  providers: (s) => s.providers,
  isLoading: (s) => s.isLoading,
  error: (s) => s.error,
  summaries: (s) => s.portfolio.summaries_by_currency,
  populatedCurrencies: (s) =>
    SUPPORTED_CURRENCIES.filter((currency) =>
      Boolean(s.portfolio.summaries_by_currency[currency]),
    ),
  selectedCurrencyView: (s) => s.selectedCurrencyView,
};

export default { namespaced: true, state, mutations, actions, getters };
